import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  DarkMode,
  LightMode,
  Download,
  GitHub,
  KeyboardArrowDown,
  Description,
  Terminal,
  Security,
  MenuBook,
  Build,
  School,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { getAllDocumentationSections } from '../data/documentation';
import UpperNavigation from './UpperNavigation';

// Sticky navbar that stays visible on scroll
function StickyNavbar({ children }) {
  return children;
}

const ExceptionalNavigation = (props) => {
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [anchorElDocs, setAnchorElDocs] = useState(null);
  const [anchorElProduct, setAnchorElProduct] = useState(null);
  const [anchorElResources, setAnchorElResources] = useState(null);
  const [anchorElCompany, setAnchorElCompany] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const docSections = getAllDocumentationSections();

  const getSectionIcon = (key) => {
    const iconMap = {
      'getting-started': <Description fontSize="small" />,
      'cli': <Terminal fontSize="small" />,
      'security': <Security fontSize="small" />,
      'user-guide': <MenuBook fontSize="small" />,
      'developers': <Build fontSize="small" />,
      'tutorials': <School fontSize="small" />,
    };
    return iconMap[key] || <MenuBook fontSize="small" />;
  };

  // Navigation items
  const navigationItems = [
    {
      label: 'Product',
      items: [
        { label: 'Features', path: '/#features', description: 'Comprehensive password management', isHashLink: true },
        { label: 'Security', path: '/#security', description: 'Zero-knowledge architecture', isHashLink: true },
        { label: 'Security Tools', path: '/tools', description: 'Online password generator and utilities' },
        { label: 'Screenshots', path: '/#screenshots', description: 'See PWGen-Rust in action', isHashLink: true },
        { label: 'Pricing', path: '/pricing', description: 'Free and open source' },
      ]
    },
    {
      label: 'Documentation',
      isSpecial: true,
      items: docSections.slice(0, 6).map(section => ({
        label: section.title,
        path: `/docs/${section.key}/${section.pages[0]}`,
        description: section.description,
        icon: getSectionIcon(section.key)
      }))
    },
    {
      label: 'Resources',
      items: [
        { label: 'Blog', path: '/blog', description: 'Latest news and updates' },
        { label: 'Community', path: 'https://github.com/KiefStudioMA/PWGen-Rust/discussions', description: 'Join the discussion', external: true },
        { label: 'Support', path: 'https://github.com/KiefStudioMA/PWGen-Rust/issues', description: 'Get help', external: true },
        { label: 'Changelog', path: 'https://github.com/KiefStudioMA/PWGen-Rust/releases', description: 'Release notes', external: true },
      ]
    },
    {
      label: 'Company',
      items: [
        { label: 'About', path: '/about', description: 'Our mission and team' },
        { label: 'Careers', path: '/careers', description: 'Join our team' },
        { label: 'Contact', path: '/contact', description: 'Get in touch' },
        { label: 'Press Kit', path: '/press', description: 'Media resources' },
        { label: 'Licensing', path: '/licensing', description: 'Legal terms and ownership' },
      ]
    },
  ];

  // Event handlers

  const handleOpenMenu = (menuType) => (event) => {
    switch (menuType) {
      case 'docs':
        setAnchorElDocs(event.currentTarget);
        break;
      case 'product':
        setAnchorElProduct(event.currentTarget);
        break;
      case 'resources':
        setAnchorElResources(event.currentTarget);
        break;
      case 'company':
        setAnchorElCompany(event.currentTarget);
        break;
      default:
        break;
    }
  };

  const handleCloseMenu = (menuType) => () => {
    switch (menuType) {
      case 'docs':
        setAnchorElDocs(null);
        break;
      case 'product':
        setAnchorElProduct(null);
        break;
      case 'resources':
        setAnchorElResources(null);
        break;
      case 'company':
        setAnchorElCompany(null);
        break;
      default:
        break;
    }
  };

  const handleHashNavigation = (path) => {
    const [routePath, hash] = path.split('#');
    if (location.pathname !== routePath) {
      // Navigate to the page first, then scroll with retry mechanism
      navigate(routePath);
      
      // Retry mechanism to ensure element exists before scrolling
      const scrollToElement = (retries = 0) => {
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (retries < 10) {
            // Retry with exponential backoff: 100ms, 200ms, 400ms, etc.
            setTimeout(() => scrollToElement(retries + 1), 100 * Math.pow(1.5, retries));
          }
        }
      };
      
      // Start the retry process
      setTimeout(() => scrollToElement(), 100);
    } else {
      // Already on the page, just scroll
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const renderMenu = (items, anchorEl, onClose, menuType) => (
    <Menu
      id={`${menuType}-menu`}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      keepMounted={false}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={Boolean(anchorEl)}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        elevation: 8,
        sx: {
          mt: 1.5,
          borderRadius: 2,
          minWidth: 280,
          maxWidth: 400,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          '& .MuiList-root': {
            py: 1,
          },
          '& .MuiMenuItem-root': {
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        },
      }}
    >
      {items.map((item, index) => (
        <MenuItem
          key={index}
          onClick={(e) => {
            onClose();
            if (item.isHashLink) {
              e.preventDefault();
              handleHashNavigation(item.path);
            }
          }}
          component={item.external || item.isHashLink ? 'div' : RouterLink}
          to={!item.external && !item.isHashLink ? item.path : undefined}
          href={item.external ? item.path : undefined}
          target={item.external ? '_blank' : undefined}
          sx={{ py: 1.5, cursor: 'pointer' }}
        >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
          )}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography variant="caption" color="text.secondary">
                {item.description}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <List>
        {navigationItems.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            <ListItem>
              <ListItemText
                primary={section.label}
                primaryTypographyProps={{
                  variant: 'subtitle2',
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              />
            </ListItem>
            {section.items.map((item, itemIndex) => (
              <ListItem key={itemIndex} disablePadding>
                <ListItemButton
                  component={item.external || item.isHashLink ? 'div' : RouterLink}
                  to={!item.external && !item.isHashLink ? item.path : undefined}
                  href={item.external ? item.path : undefined}
                  target={item.external ? '_blank' : undefined}
                  onClick={item.isHashLink ? () => handleHashNavigation(item.path) : undefined}
                  sx={{ pl: 3, cursor: 'pointer' }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {sectionIndex < navigationItems.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ px: 2, pb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isDarkMode}
              onChange={toggleTheme}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isDarkMode ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
              Dark Mode
            </Box>
          }
        />

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            href="https://github.com/KiefStudioMA/PWGen-Rust"
            target="_blank"
            startIcon={<GitHub />}
            size="small"
            fullWidth
          >
            GitHub
          </Button>
          <Button
            variant="contained"
            component={RouterLink}
            to="/download"
            startIcon={<Download />}
            size="small"
            fullWidth
            sx={{
              bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
              color: theme.palette.accent?.contrastText || 'white',
              '&:hover': {
                bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
              },
            }}
          >
            Download
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Upper Navigation Bar */}
      <UpperNavigation />
      
      <StickyNavbar {...props}>
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(17, 24, 39, 0.98)'
              : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(24px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            top: 0,
            width: '100%',
            zIndex: theme.zIndex.appBar + 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {/* Logo */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <Box
                  component="img"
                  src={theme.palette.mode === 'dark' 
                    ? "/assets/images/PWGenLogo-Dark-Wide.png" 
                    : "/assets/images/PWGenLogo-Wide.png"
                  }
                  alt="PWGen-Rust"
                  sx={{
                    height: 40,
                  }}
                />
              </Box>

              {/* Desktop Menu */}
              <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' }, justifyContent: 'flex-end', mr: 4 }}>
                {navigationItems.map((item, index) => {
                  const menuType = item.label.toLowerCase();
                  const anchorEl = {
                    product: anchorElProduct,
                    documentation: anchorElDocs,
                    resources: anchorElResources,
                    company: anchorElCompany,
                  }[menuType];

                  return (
                    <Box key={index}>
                      <Button
                        onClick={handleOpenMenu(menuType === 'documentation' ? 'docs' : menuType)}
                        sx={{
                          my: 2,
                          color: 'text.primary',
                          display: 'flex',
                          alignItems: 'center',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {item.label}
                        <KeyboardArrowDown sx={{ ml: 0.5 }} />
                      </Button>
                      {renderMenu(
                        item.items,
                        anchorEl,
                        handleCloseMenu(menuType === 'documentation' ? 'docs' : menuType),
                        menuType === 'documentation' ? 'docs' : menuType
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Desktop Actions */}
              <Box sx={{ flexGrow: 0, display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
                <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'}>
                  <IconButton onClick={toggleTheme} sx={{ color: 'text.primary' }}>
                    {isDarkMode ? <LightMode /> : <DarkMode />}
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  href="https://github.com/KiefStudioMA/PWGen-Rust"
                  target="_blank"
                  startIcon={<GitHub />}
                  sx={{ ml: 1 }}
                >
                  GitHub
                </Button>

                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/download"
                  startIcon={<Download />}
                  sx={{ 
                    ml: 1,
                    bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                    color: theme.palette.accent?.contrastText || 'white',
                    '&:hover': {
                      bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                    },
                  }}
                >
                  Download
                </Button>
              </Box>

              {/* Mobile menu button */}
              <Box sx={{ flexGrow: 0, display: { xs: 'flex', lg: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleDrawerToggle}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </StickyNavbar>

      {/* Mobile Drawer */}
      <nav>
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          PaperProps={{
            sx: {
              width: 320,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>

    </>
  );
};

export default ExceptionalNavigation;